import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { WIZARD_STEPS, useConfigurator, type WizardStep } from '../../../store/configurator';
import { ProgressHeader } from '../ProgressHeader';
import { WizardFooter } from '../WizardFooter';
import { PreviewPane } from '../PreviewPane';
import { Logo } from '../Logo';
import { MainViewportSlide } from './MainViewportSlide';
import { Shell, LogoSlot, Side, Scroll, Body, Intro, Title, Description } from './WizardLayout.styles';

export type WizardLayoutProps = {
  /** Zero-based index of the current step. */
  currentStep: number;
  /** Heading + description shown above the step body. */
  title: string;
  description: string;
  children: React.ReactNode;
  /** Main viewport (left). Defaults to the device preview. */
  main?: React.ReactNode;
  /** Optional content rendered in the footer, above the back/next buttons. */
  footerExtra?: React.ReactNode;
  /** Optional full-pane overlay (e.g. the themes catalog) rendered above the side pane. */
  overlay?: React.ReactNode;
  nextDisabled?: boolean;
  /** Override the primary button label. */
  nextLabel?: string;
  /** Override the primary button action (defaults to navigating to the next step). */
  onNext?: () => void;
};

export const WizardLayout = ({
  currentStep,
  title,
  description,
  children,
  main,
  footerExtra,
  overlay,
  nextDisabled,
  nextLabel: nextLabelProp,
  onNext,
}: WizardLayoutProps): React.ReactElement => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { saveNow } = useConfigurator();

  // Step names are a fixed, closed vocabulary (Theme / Colors & Art Style / Payment review /
  // Payment). WIZARD_STEPS stays English at module scope (it also keys navigation/paths and is
  // consumed by the mobile flows); translate at this render site, keyed on the stable
  // `step.key`, so the label stays reactive on a language change.
  const stepName = (step: WizardStep): string => {
    switch (step.key) {
      case 'theme':
        return t('Theme');
      case 'colors':
        return t('Style');
      case 'review':
        return t('Review');
      case 'payment':
        return t('Payment');
      default:
        return step.name;
    }
  };

  const isLast = currentStep === WIZARD_STEPS.length - 1;
  const nextStep = WIZARD_STEPS[currentStep + 1];
  const nextLabel =
    nextLabelProp ??
    (isLast ? t('Finish') : t('Continue to {{step}}', { step: stepName(nextStep).toLowerCase() }));

  const goNext = (): void => {
    // Persist the full configuration on every step advance — including the finish step's
    // "Continue to checkout" (which overrides onNext) — so each step is saved as the user
    // moves forward, not only when a value changes.
    saveNow();
    if (onNext) onNext();
    else if (nextStep) navigate(nextStep.path);
  };
  const goBack = (): void => {
    const prev = WIZARD_STEPS[currentStep - 1];
    if (prev) navigate(prev.path);
  };

  // The default main viewport is the live device preview (design step). Its iframe
  // floats in the persistent host BELOW the chrome, so this shell goes click-through
  // and the interactive islands (logo, sidebar, device switcher) re-enable themselves,
  // letting the wheel reach the iframe so the site inside can be scrolled. Steps that
  // pass their own `main` (interactive promo cards, the finish CTA) keep the chrome
  // fully interactive.
  const devicePreview = main == null;

  return (
    <Shell $previewInteractive={devicePreview}>
      {/* Brand lockup sits OUTSIDE the sliding viewport so it stays fixed top-left
          while the preview slides in on each step change. */}
      <LogoSlot>
        <Logo />
      </LogoSlot>

      {/* Main viewport (left preview) slides in on each desktop step change;
          the sidebar/chrome below stay put. Keyed on the step index so a back
          navigation slides in from the opposite side. */}
      <MainViewportSlide step={currentStep}>{main ?? <PreviewPane />}</MainViewportSlide>

      <Side>
        <Scroll>
          <ProgressHeader currentStep={currentStep} />

          <Body>
            <Intro>
              <Title text={title} tag="h1" />
              <Description text={description} tag="paragraph" />
            </Intro>

            {children}
          </Body>

          <WizardFooter
            nextLabel={nextLabel}
            onNext={goNext}
            onBack={goBack}
            showBack={currentStep > 0}
            nextDisabled={nextDisabled}
            extra={footerExtra}
          />
        </Scroll>

        {overlay}
      </Side>
    </Shell>
  );
};

export default WizardLayout;
